import { deepCopy } from '@ethersproject/properties'
// This is the only file which should instantiate new Providers.
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { StaticJsonRpcProvider } from '@ethersproject/providers'
import { getChainId, getChainInfo, Network, SupportedNetwork } from '@spritz-finance/sdk'
import { isSerializable } from '../../utils/isSerializable'

import { getRpcUrls } from './constants'

class AppJsonRpcProvider extends StaticJsonRpcProvider {
    private _blockCache = new Map<string, Promise<any>>()
    get blockCache() {
        // If the blockCache has not yet been initialized this block, do so by
        // setting a listener to clear it on the next block.
        if (!this._blockCache.size) {
            this.once('block', () => this._blockCache.clear())
        }
        return this._blockCache
    }

    constructor(network: SupportedNetwork) {
        const chainInfo = getChainInfo(network)
        super(getRpcUrls(network)[0], {
            chainId: chainInfo.chainId,
            name: chainInfo.name,
        })
        this.pollingInterval = 12000 //12 seconds
    }

    send(method: string, params: Array<any>): Promise<any> {
        // Only cache eth_call's.
        if (method !== 'eth_call') return super.send(method, params)

        // Only cache if params are serializable.
        if (!isSerializable(params)) return super.send(method, params)

        const key = `call:${JSON.stringify(params)}`
        const cached = this.blockCache.get(key)
        if (cached) {
            this.emit('debug', {
                action: 'request',
                request: deepCopy({ method, params, id: 'cache' }),
                provider: this,
            })
            return cached
        }

        const result = super.send(method, params)
        this.blockCache.set(key, result)
        return result
    }
}

/**
 * These are the only JsonRpcProviders used directly by the interface.
 */
export const RPC_PROVIDERS: Record<number, StaticJsonRpcProvider> = {
    [getChainId(Network.Ethereum)]: new AppJsonRpcProvider(Network.Ethereum),
    [getChainId(Network.Binance)]: new AppJsonRpcProvider(Network.Binance),
    [getChainId(Network.Polygon)]: new AppJsonRpcProvider(Network.Polygon),
    [getChainId(Network.Optimism)]: new AppJsonRpcProvider(Network.Optimism),
    [getChainId(Network.Arbitrum)]: new AppJsonRpcProvider(Network.Arbitrum),
    [getChainId(Network.Avalanche)]: new AppJsonRpcProvider(Network.Avalanche),
}

export const getRpcProviderForNetwork = (network: SupportedNetwork) => {
    return RPC_PROVIDERS[getChainId(network)]
}
